import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  HostListener,
  Renderer2,
  AfterViewInit,
  ViewChild,
  ElementRef,
  inject,
  DestroyRef,
} from "@angular/core";
import {
  FilterInitDataKeys,
  DoubleFilterKeys,
  FilterService,
  SortKeys, statusTypes, singleFilterKeys, redistributionStatusTypes,
} from "./filter.service";
import { createISODateFromString } from "@app/utils/date.util";
import { Dropdown } from "bootstrap";
import {InputComponent} from "@app/input/input.component";
import {catchError, of} from "rxjs";
import {DataService} from "@app/shared/data.service";
import {OrganisationInterface} from "@app/quarantine/intrfaces/quarantine.interface";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: "app-filter",
  templateUrl: "./filter.component.html",
  styleUrls: ["./filter.component.scss"],
})
export class FilterComponent implements OnInit, AfterViewInit {
  destroyRef = inject(DestroyRef);
  
  @Input() reset: boolean = false;
  @Input() key: FilterInitDataKeys = "";
  @Input() sortKey?: string = "";
  @Input() preselectedOption?: { [key: string]: any } = {};
  @Output() selectedOptionsChange = new EventEmitter<{
    key: string;
    payload: string[] | { [key: string]: any };
  }>();
  @Output() sortChange = new EventEmitter<{ [key: string]: "ASC" | "DESC" }>();
  @Output() onError = new EventEmitter<{ [key: string]: any }>();
  @ViewChild("dropdown", { static: true }) dropdownRef!: ElementRef;

  doubleFilterKeys = DoubleFilterKeys;
  singleFilterKeys = singleFilterKeys;
  sortKeys = SortKeys;
  filterOptions: Array<any> = [];
  visibleOptions: Array<any> = [];
  isDoubleInputFilter: boolean = false;
  isSimpleInput: boolean = false;
  filterPlaceholders: { placeholder: string; placeholder2: string } = {
    placeholder: "",
    placeholder2: "",
  };
  maxSymbols: number = 999;
  isActive: boolean = false;
  loading = false;
  
  listToRender: string [] = [];

  private dropdown!: Dropdown;

  constructor(
    private filterService: FilterService,
    private dataService: DataService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (
        this.key.length
        && !(this.key === "tradeName"
            // @ts-ignore
          || this.key == "documentStatus"
          || this.key === "redistributionStatus"
          || this.key === "series"
          || this.key === "institutionIds"
          || this.key === "date-range"
        )
    ) {
      this.filterService.getInitialFilterData(this.key)?.subscribe(
        (filtersData: string[]) => {
          if (!filtersData || !filtersData.length) return;
          if (this.preselectedOption && this.preselectedOption[this.key]) {
            this.filterOptions = [
              filtersData.find(
                (option) =>
                  this.preselectedOption &&
                  option === this.preselectedOption[this.key]
              ),
            ];
          } else {
            this.filterOptions = filtersData;
          }
          this.visibleOptions = [...this.filterOptions];
        },
        (error) => {
          this.onError.emit({
            text: "Error fetching filters data",
            payload: error,
          });
          console.error("Error fetching filters data", error);
        }
      );
    }
    
    if (this.key === "institutionIds") {
      this.dataService.allOrganizationsValue
          .subscribe((data) => {
            this.filterOptions = data.data
                .reduce((acc: any[], curr: OrganisationInterface) => {
                  if (curr.type === 'ZOZ') {
                    acc.push({id: curr.id, fullName: curr.fullName});
                  }
              
              let stack = [curr.children]
              while (stack.length) {
                let children = stack.pop();
                
                if (children?.length) {
                  children.forEach((child: OrganisationInterface) => {
                    if (child.type === 'ZOZ') {
                      acc.push({id: child.id, fullName: child.fullName});
                    }
                    
                    if (child.children.length) {
                      stack.push(child.children);
                    }
                  })
                }
                
              }
              
              return acc;
            }, []);
            
            this.visibleOptions = [...this.filterOptions];
          })
    }
    
    if (this.key === "institution-tax") {
      this.dataService.showMyOrganizationValue
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((data: boolean) => {
            if (data) {
              let userOrganizations=  this.dataService.userOrganizationsValue.value.map((item: any) => item.code)
              this.visibleOptions = this.filterOptions.filter((option) => userOrganizations.includes(option));
            } else {
              this.visibleOptions = [...this.filterOptions];
            }
            
            this.reset = true;
            setTimeout(() => {
              this.reset = false;
            }, 0);
      });
    }
    
    this.isDoubleInputFilter = this.doubleFilterKeys.includes(this.key);
    this.isSimpleInput = this.singleFilterKeys.includes(this.key);
    this.maxSymbols = this.doubleFilterKeys.includes(this.key) ? 7 : 999;
    this.filterPlaceholders = {
      placeholder: this.isDoubleInputFilter
        ? this.key === "due-date" || this.key === "date-range"
          ? "Від (ММ.РРРР)"
          : "Від"
        : "Пошук",
      placeholder2: this.isDoubleInputFilter
        ? this.key === "due-date" || this.key === "date-range"
          ? "До (ММ.РРРР)"
          : "До"
        : "",
    };
  }

  ngAfterViewInit(): void {
    this.dropdown = new Dropdown(this.dropdownRef.nativeElement, {
      popperConfig: {
        placement: "bottom-start",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 5],
            },
          },
        ],
      },
    });
  }

  get getListToRender(): string[] {
    if (typeof this.visibleOptions[0] === "string") {
      return this.visibleOptions;
    }

    switch (this.key) {
      case "sponsor":
        return this.visibleOptions.map((option) => option.sponsoredBy);
      case "nomenclature":
        return this.visibleOptions.map((option) => option.mnnName);
      case "type":
        return this.visibleOptions.map((option) => option.name);
      case 'documentStatus': {
        return Object.keys(statusTypes);
      }
      case 'redistributionStatus': {
        return Object.keys(redistributionStatusTypes);
      }
      case "tradeName":
        return this.listToRender;
      case "institutionIds":
        return this.visibleOptions.map((option) => option.fullName);
      default:
        return [];
    }
  }

  searchOptions(searchString: string): void {
    if (!searchString) {
      this.visibleOptions = [...this.filterOptions];
      
      if (this.key === "institution-tax") {
        if (this.dataService.showMyOrganizationValue.value) {
          let userOrganizations=  this.dataService.userOrganizationsValue.value.map((item: any) => item.code)
          this.visibleOptions = this.filterOptions.filter((option) => userOrganizations.includes(option));
        } else {
          this.visibleOptions = [...this.filterOptions];
        }
      }
      return;
    }
    
    if (this.key === "institution-tax") {
      if (this.dataService.showMyOrganizationValue.value) {
        let userOrganizations=  this.dataService.userOrganizationsValue.value.map((item: any) => item.code)
        let res = this.filterOptions.filter((option) => userOrganizations.includes(option));
        this.visibleOptions = res.filter((option) => option.toLowerCase().includes(searchString.toLowerCase()));
      } else {
        this.visibleOptions = this.filterOptions.filter((option) => option.toLowerCase().includes(searchString.toLowerCase()));
      }
      return;
    }
    
    this.visibleOptions = this.filterOptions.filter((option) => {
      if (typeof option === "string") {
        return option.toLowerCase().includes(searchString.toLowerCase());
      }
      switch (this.key) {
        case "sponsor":
          return option.sponsoredBy
            .toLowerCase()
            .includes(searchString.toLowerCase());
        case "nomenclature":
          return option.mnnName
            .toLowerCase()
            .includes(searchString.toLowerCase());
        case "institutionIds":
          return option.fullName
            .toLowerCase()
            .includes(searchString.toLowerCase());
        case "type":
          return option.name.toLowerCase().includes(searchString.toLowerCase());
        default:
          return false;
      }
    });
  }

  onCheckedListChange(checkedUpdate: string[]): void {
    if (!checkedUpdate.length) {
      this.reset = true;
      setTimeout(() => {
        this.reset = false;
      }, 0);
    }
    this.isActive = checkedUpdate.length > 0;
    switch (this.key) {
      case "sponsor":
        this.selectedOptionsChange.emit({
          key: this.key,
          payload: checkedUpdate.map(
            (option) =>
              this.filterOptions.find((item) => item.sponsoredBy === option)
                .sponsorId
          ),
        });
        break;
      case "nomenclature":
        this.selectedOptionsChange.emit({
          key: this.key,
          payload: checkedUpdate.map(
            (option) =>
              this.filterOptions.find((item) => item.mnnName === option)
                .nomenclatureId
          ),
        });
        break;
      case "type":
        this.selectedOptionsChange.emit({
          key: this.key,
          payload: checkedUpdate.map(
            (option) =>
              this.filterOptions.find((item) => item.name === option).id
          ),
        });
        break;
      case "documentStatus": {
        let res: string[] = [];
        checkedUpdate.forEach(item => {
          res.push(statusTypes[item]);
        });
        this.selectedOptionsChange.emit({
          key: 'status',
          payload: res
        });
        break;
      }
      case "redistributionStatus": {
        let res: string[] = [];
        checkedUpdate.forEach(item => {
          res.push(redistributionStatusTypes[item]);
        });
        this.selectedOptionsChange.emit({
          key: 'redistributionStatus',
          payload: res
        });
        break;
      }
      case "institutionIds": {
        let res: string[] = [];
        checkedUpdate.forEach(item => {
          res.push(this.filterOptions.find((org) => org.fullName === item).id);
        });
        this.selectedOptionsChange.emit({
          key: 'institutionIds',
          payload: res
        });
        break;
      }
      case "tradeNameUnique": {
        this.selectedOptionsChange.emit({
          key: "tradeName",
          payload: checkedUpdate,
        });
        break;
      }
      
      default:
        this.selectedOptionsChange.emit({
          key: this.key,
          payload: checkedUpdate,
        });
    }
  }

  onSearchInputChange(
    payload: string | { from?: string; to?: string; isExpired?: boolean }
  ): void {
    if (typeof payload === "string" && this.key === "series") {
      this.isActive = !!payload.length;
      this.selectedOptionsChange.emit({
        key: this.key,
        payload: [payload]
      });
      return;
    }
    
    if (typeof payload === "string" && this.key === "tradeName") {
      this.asyncSearchTradingName(payload);
      return;
    }
    
    if (typeof payload === "string") this.searchOptions(payload as string);
    else {
      this.isActive = !!Object.values(payload).find(
        (value) =>
          value === true || (typeof value === "string" && value.length > 0)
      );
      if (this.key === "due-date") {
        this.selectedOptionsChange.emit({
          key: this.key,
          payload: {
            ...(payload.from && {
              from: createISODateFromString(payload.from),
            }),
            ...(payload.to && { to: createISODateFromString(payload.to) }),
          },
        });
      } else if (this.key === "date-range") {
        this.selectedOptionsChange.emit({
          key: 'due-date',
          payload: {
            ...(payload.from && {
              from: createISODateFromString(payload.from),
            }),
            ...(payload.to && { to: createISODateFromString(payload.to) }),
          },
        });
      } else {
        this.selectedOptionsChange.emit({
          key: this.key,
          payload,
        });
      }
    }
  }

  onSortChange(payload: { [key: string]: "ASC" | "DESC" }): void {
    this.sortChange.emit(payload);
  }

  inputCheckFunction(keyword: string): boolean {
    switch (this.key) {
      case "due-date":
      case "date-range":
        if (keyword.length === 0) return true;
        const dateFormatRegex = /^(?:0[1-9]|1[0-2])[./-]\d{4}$|^$/;
        return dateFormatRegex.test(keyword.trim());
      case "balance-quantity":
      case "institution-tax":
        const numbersOnlyRegex = /^\d*$/;
        return numbersOnlyRegex.test(keyword.trim());
      default:
        return true;
    }
  }

  getErrorMessage(): string {
    switch (this.key) {
      case "due-date":
      case 'date-range':
        return "Неправильний формат дати. Введіть дату у форматі ММ.РРРР (місяць.рік).";
      case "balance-quantity":
      case "institution-tax":
        return "Введені дані некоректні. Перевірте їх та спробуйте ще раз";
      default:
        return "Введені дані некоректні. Перевірте їх та спробуйте ще раз.";
    }
  }

  isColumnSortable(columnKey: string): boolean {
    return (
      Object.keys(this.sortKeys).includes(columnKey) &&
      columnKey === this.sortKey
    );
  }
  
   asyncSearchTradingName(value: string) {
    if (value.length < 3) {
      this.listToRender = [];
      return;
    }
    this.loading = true;
    this.filterService.getMedicinesMedicalDevices({tradeName: value})
        .pipe(
            catchError(error => {
              console.error(error);
              this.loading = false;
              return of([]); // вернёт пустой массив вместо ошибки
            })
        )
        .subscribe((res) => {
            this.listToRender = [...new Set(res.map(item => item.tradeName))];
            this.loading = false;
        });
    
  }

  dropdownOpen(event: MouseEvent): void {
    const dropdownElement = this.dropdownRef.nativeElement;
    if (dropdownElement.classList.contains("show")) {
      this.dropdown.hide();
    } else {
      document.querySelectorAll(".dropdown-toggle.show")?.forEach(() => {
        document.querySelector("body")?.click();
      });
      this.dropdown.show();
    }
  }
}
